"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  Server,
  ClipboardCheck,
  BookOpen,
  Settings,
  Building2,
  User,
  LogOut,
} from "lucide-react";
import { authClient } from "@/lib/auth/client";

// Create a context to expose the open/close functionality
interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const CommandPaletteContext = React.createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette() {
  const context = React.useContext(CommandPaletteContext);
  if (!context) {
    throw new Error("useCommandPalette must be used within CommandPalette");
  }
  return context;
}

interface CommandPaletteProps {
  children?: React.ReactNode;
}

export function CommandPalette({ children }: CommandPaletteProps) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runCommand = React.useCallback((command: () => void) => {
    setOpen(false);
    command();
  }, []);

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      {mounted && (
        <CommandDialog open={open} onOpenChange={setOpen}>
          <CommandInput placeholder="Type a command or search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup heading="Navigation">
              <CommandItem
                onSelect={() => runCommand(() => router.push("/dashboard"))}
              >
                <LayoutDashboard className="mr-2 h-4 w-4" />
                <span>Dashboard</span>
              </CommandItem>
              <CommandItem
                onSelect={() => runCommand(() => router.push("/systems"))}
              >
                <Server className="mr-2 h-4 w-4" />
                <span>Systems</span>
              </CommandItem>
              <CommandItem
                onSelect={() => runCommand(() => router.push("/assessments"))}
              >
                <ClipboardCheck className="mr-2 h-4 w-4" />
                <span>Assessments</span>
              </CommandItem>
              <CommandItem
                onSelect={() => runCommand(() => router.push("/framework"))}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                <span>Framework</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Settings">
              <CommandItem
                onSelect={() => runCommand(() => router.push("/settings/profile"))}
              >
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </CommandItem>
              <CommandItem
                onSelect={() => runCommand(() => router.push("/settings/profile"))}
              >
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </CommandItem>
              <CommandItem
                onSelect={() => runCommand(() => router.push("/organisations/select"))}
              >
                <Building2 className="mr-2 h-4 w-4" />
                <span>Switch Organisation</span>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Account">
              <CommandItem
                onSelect={async () => {
                  setOpen(false);
                  try {
                    await authClient.signOut();
                    window.location.href = '/login';
                  } catch (error) {
                    console.error('Sign out error:', error);
                    // Even if sign out fails, redirect to login
                    window.location.href = '/login';
                  }
                }}
                className="text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </CommandDialog>
      )}
    </CommandPaletteContext.Provider>
  );
}
